import { merge, get, pick, find, findIndex } from "lodash";

import { SaleEditModel } from "../../sales/edit/sale-edit.model";
import { onlineT, OnlineCaution } from "../../typings/online";
import { paymentT } from "../../typings/payment";
import { Enums, Events } from "../../thor.enums";
import { MAX_ONLINE_ITEMS } from "../../thor.const";
import { isDeleted, isDeposit } from "../../utils/payment";
import { postMessageToV2 } from "../../utils/sale-iframe";

const INTERVAL_DURATION = 1000;

export class OnlineContainerEditModel extends SaleEditModel {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$q",
        "$rootScope",
        "$state",
        "$timeout",
        "$translate",
        "absenteeBidsService",
        "authService",
        "categoryService",
        "contractService",
        "gatewayApi",
        "itemService",
        "liveService",
        "onlineContainerService",
        "onlineDateService",
        "paymentService",
        "searchService",
        "sitesService",
        "uploadService",
        "utilsService",
        "timingService",
        "toaster",
        "configService"
    ];

    public endDateTimeout: string;
    public urlIframe: string;

    protected authorizedAuctionType: any = [Enums.AuctionType.Online];
    protected isReadOnly: boolean;
    protected onlineCaution: OnlineCaution;

    private hasSaleStarted = null;
    private salePaymentDeposit: paymentT.Payment[] = [];

    constructor(
        $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        $q: ng.IQService,
        $rootScope: ng.IRootScopeService,
        protected $state: ng.ui.IStateService,
        _$timeout,
        $translate,
        _absenteeBidsService,
        _authService,
        categoryService,
        _contractService,
        gatewayApi,
        itemService,
        liveService,
        private onlineContainerService,
        private onlineDateService,
        private paymentService,
        _searchService,
        _sitesService,
        uploadService,
        utils,
        private timing,
        private toaster,
        _configService
    ) {
        super(
            $log,
            $q,
            $rootScope,
            $state,
            _$timeout,
            $translate,
            _absenteeBidsService,
            _authService,
            categoryService,
            _contractService,
            gatewayApi,
            itemService,
            liveService,
            onlineContainerService,
            _searchService,
            _sitesService,
            uploadService,
            utils,
            _configService
        );

        const checkAllOnline = () => {
            this.checkIfEveryMandatoryFieldsAreFilled();
            this.checkIsPublishable();
            this.checkIsReadOnly();
        };

        $rootScope.$on(Events.OnlineSaleUpdated, checkAllOnline);

        $rootScope.$on(Events.OnlineSalePurged, checkAllOnline);

        $rootScope.$on(Events.OnlineSalePaymentsUpdated, () => this.fetchPayments());

        $rootScope.$on(Events.PostMessageToV2, () => postMessageToV2({ saleId: this.id }));
    }

    public saveAddressFields(fields) {
        const mandatoryFields = [
            "address.formatted_address",
            "address.street",
            "address.postcode",
            "address.city",
            "address.street_number",
            "address.route",
            "address.location"
        ];

        const data = pick(this.originalResource, mandatoryFields);
        merge(data, pick(this.resource, fields));

        return this.onlineContainerService
            .update(this.id, data)
            .then(res => {
                this.$log.info("Container address fields updated with success", res, fields);
                this.resource = this.resourceFormatters(res);
                this.originalResource = angular.copy(this.resource);
                this.$rootScope.$emit(Events.OnlineSaleUpdated);
            })
            .catch(error => {
                this.$log.error("Error while updating address fields", error, fields);
                return this.$q.reject(error);
            });
    }

    public updateShippingConditions(shippingConditionsId) {
        const originalShippingConditionsId = get(this.originalResource, "shipping_conditions.id");
        return this.onlineContainerService
            .updateShippingConditions(this.id, originalShippingConditionsId, shippingConditionsId)
            .then(shippingConditions => {
                this.$log.info(
                    `Sale ${this.id} online containers conditions updated with success:`,
                    shippingConditions
                );
                this.resource.shipping_conditions = pick(shippingConditions, ["id", "url"]);
                this.originalResource.shipping_conditions = angular.copy(this.resource.shipping_conditions);
                this.$rootScope.$emit(Events.SaleRelatedObjectUpdated);
            });
    }

    public createItem(itemPost) {
        return this.onlineContainerService.createItem(this.id, itemPost);
    }

    public async insertItem(item) {
        this.isSaving = true;
        this.items.unshift(item);
        await this.getSaleTranslationStatus();
        this.$rootScope.$broadcast(Events.OnlineItemUpdated, { fetchItems: true });
        this.$rootScope.$broadcast(Events.PostMessageToV2);
        this.isSaving = false;
    }

    public save(field) {
        this.isSaving = true;

        return this.onlineContainerService
            .update(this.id, this.setFieldDataOnSave(field))
            .then(async res => {
                this.$log.info(`Container updated with success: ${field}`, res);
                this.resource = this.resourceFormatters(res);
                this.originalResource = angular.copy(this.resource);

                await this.getSaleTranslationStatus();

                this.$rootScope.$emit(Events.OnlineSaleUpdated);
                this.checkOnlineItemsMandatoryFields();
            })
            .catch(error => {
                this.$log.error("Error while updating the container", error);
                return this.$q.reject(error);
            })
            .finally(() => (this.isSaving = false));
    }

    public resetDate() {
        if (this.resource?.start_at) {
            this.resource.start_at = null;

            return this.save("start_at")
                .then(() => this.toaster.show(
                    this.$t("suite_a_vos_modifications_la_date_de_mise_en_ligne_a_ete_supprimee_merci_de_la_completer")
                ));
        }
        return this.$q.resolve();
    }

    public fetchItems() {
        this.items = [];
        this.itemsLoading = true;
        this.itemsLoadError = false;

        return this.onlineContainerService
            .getItems(this.id)
            .then(items => {
                this.items = items;

                this.endDateTimeout = this.setEndDateTimeout();

                const { isPast, saleStarted } = this.resource || {};
                const promises = [];

                if (isPast || saleStarted) {
                    promises.push(this.fetchOnlineContainerLastBids());
                }
                if (isPast) {
                    promises.push(this.fetchPayments());
                }

                return this.$q.all(promises);
            })
            .catch(error => {
                this.items = [];
                this.itemsLoading = false;
                this.itemsLoadError = true;
                return this.$q.reject(error);
            })
            .finally(() => {
                this.itemsLoading = false;
                this.$rootScope.$emit(Events.OnlineItemsLoaded);
            });
    }

    public fetchOnlineCaution() {
        if (!this.isOnlineSale()) {
            return this.$q.resolve();
        }

        return this.$q.all([this.onlineContainerService.getOnlineContainerCaution(this.id)]).then(([onlineCaution]) => {
            this.onlineCaution = onlineCaution;
            return this.$q.resolve();
        });
    }

    public fetchAll({ checkSaleTranslationStatus } = { checkSaleTranslationStatus: true }) {
        return this.$q
            .all([this.fetchSale(), this.fetchItems(), this.fetchOnlineCaution()])
            .then(() => {
                if (checkSaleTranslationStatus) {
                    this.getSaleTranslationStatus();
                }
                this.setSubscriptionsCount();
                this.checkIfEveryMandatoryFieldsAreFilled();
                this.checkIsPublishable();
                this.checkIsReadOnly();
            })
            .catch(error => this.$log.error(`Error while fetching sale ${this.id}`, error));
    }

    public async fetchSaleParameters(
        { fetchSale, fetchSaleParameters } = { fetchSale: false, fetchSaleParameters: false }
    ) {
        await Promise.all([
            fetchSale ? this.fetchSale() : undefined,
            fetchSaleParameters && this.isOnlineSale()
                ? this.fetchOnlineCaution().then(() => this.$rootScope.$emit(Events.SaleRelatedObjectUpdated))
                : undefined
        ]);
        this.getSaleTranslationStatus();
        this.checkIfEveryMandatoryFieldsAreFilled();
        this.checkIsPublishable();
        this.checkIsReadOnly();
    }

    protected checkIsPublishable() {
        const { saleScheduled } = this.onlineDateService.getSaleStatus(this.resource);

        if (this.resource.status !== Enums.SaleStatus.Published) {
            this.isPublishable = saleScheduled;
        } else {
            this.isPublishable = this.hasNonPublishedItems();
        }
    }

    protected checkIsReadOnly() {
        const { datetime, saleStarted, isPast, status } = this.resource;

        if (!datetime) {
            this.isReadOnly = false;
            return;
        }
        this.isReadOnly = status !== Enums.SaleStatus.Created && (saleStarted || isPast);
    }

    protected checkIfEveryMandatoryFieldsAreFilled() {
        const checkFields = this.onlineContainerService.checkIfEveryMandatoryFieldsAreFilled(this.resource);
        const checkItems = this.checkItems() && this.checkOnlineItemsMandatoryFields();
        this.setItemsPremiumErrors();

        this.isEveryMandatoryFieldsFilled = Boolean(
            checkFields &&
                checkItems &&
                this.isVoluntaryContractActivated &&
                !this.itemsPublishSummary.premium_errors.length
        );
    }

    protected resourceFormatters(container) {
        const { saleStarted, salePast } = this.onlineDateService.getSaleStatus(container);

        container.saleStarted = saleStarted && container.status !== Enums.SaleStatus.Created;
        container.isPast = salePast;

        container.field = this.formatCategoryField(container.category_id);
        container.address = this.formatAddress(container.address);

        return container;
    }

    protected startCheckUpdate() {
        this.timing.setInterval(() => {
            const { start_at, datetime } = this.resource || {};

            if (start_at && datetime) {
                const { saleStarted: hasSaleStarted } = this.onlineDateService.getSaleStatus(this.resource);

                if (this.hasSaleStarted === null) {
                    this.hasSaleStarted = hasSaleStarted;
                } else if (hasSaleStarted) {
                    this.timing.stopInterval();

                    if (!this.hasSaleStarted) {
                        this.fetchAll({ checkSaleTranslationStatus: false })
                            .then(() => this.toaster.show(this.$t("mise_a_jour_des_donnees_effectuee")))
                            .catch(error => {
                                this.$log.error("Cannot update container data", error);

                                this.$mdDialog
                                    .show(
                                        this.$mdDialog
                                            .alert()
                                            .clickOutsideToClose(false)
                                            .title(this.$t("mettre_a_jour_les_informations"))
                                            .textContent(
                                                this.$t(
                                                    "afin_de_beneficier_des_dernieres_donnees_merci_de_bien_vouloir_rafraichir"
                                                )
                                            )
                                            .ok(this.$t("rafraichir_la_page"))
                                    )
                                    .finally(() => window.location.reload());
                            });
                    }
                    this.hasSaleStarted = true;
                }
            }
        }, INTERVAL_DURATION);
    }

    private checkItems(): boolean {
        return Boolean(this.items?.length && !(this.getItemsPublishCount() > MAX_ONLINE_ITEMS));
    }

    private checkOnlineItemsMandatoryFields() {
        let foundInvalidItem: boolean = false;
        this.items.forEach(item => {
            item.valid = get(item, "states.suppressed") ||
                !!(get(item, "category.field")
                && get(item, "category.id")
                && get(item, "pricing.starting_price") > 0
                && item.description
                && item.description.length >= this.itemDescriptionMinLength
                && item.media_count > 0);
            foundInvalidItem = foundInvalidItem || !item.valid;
        });
        return !foundInvalidItem;
    }

    private fetchOnlineContainerLastBids() {
        return this.onlineContainerService.getOnlineContainerLastBids(this.id)
            .then((lastBids: onlineT.LastBid[]) => {
                lastBids.map((lastBid: onlineT.LastBid) => {
                    const item: any = this.findItemById(lastBid.item_id);
                    if (item) {
                        item.pricing = {
                            ...item.pricing,
                            last_bid: lastBid
                        };
                    }
                });
                return this.$q.resolve();
            })
            .catch((error: any) =>
                this.$log.error("Error while fetching Online last-bids. Please try again later", error));
    }

    private fetchPayments() {
        return this.paymentService
            .get(this.id)
            .then((payments: paymentT.Payment[]) => {
                /*
                 * we loop through each payment to associate it to its corresponding item
                 */
                payments.map((payment: paymentT.Payment) => {
                    const { items_ids: itemsIds } = payment;

                    // check if current payment.link_payment status is 'deleted'
                    const isLinkPaymentDeleted = isDeleted(payment);

                    if (!itemsIds.length && this.onlineCaution?.caution?.amount && isDeposit(payment)) {
                        this.salePaymentDeposit.push(payment);
                    }

                    itemsIds.map((itemId: number) => {
                        const item: any = this.findItemById(itemId);

                        if (!item) {
                            return;
                        }

                        // if item.payment is undefined, we set it as an array
                        if (!item.payment) {
                            item.payment = [];
                        }

                        // check if current payment.id already exists in item.payment array
                        const paymentIndex = findIndex(item.payment, ["id", payment.id]);

                        // filter link_payment with status 'deleted'
                        if (paymentIndex === -1 && isLinkPaymentDeleted) {
                            return;
                        }

                        // remove deleted link_payment from item
                        if (paymentIndex !== -1 && isLinkPaymentDeleted) {
                            return item.payment.splice(paymentIndex, 1);
                        }

                        // update payment found in item
                        if (paymentIndex !== -1) {
                            return item.payment[paymentIndex] = payment;
                        }

                        // add payment to item
                        return item.payment.push(payment);
                    });
                });
                return this.$q.resolve();
            })
            .finally(() => this.$rootScope.$emit(Events.OnlineItemsPaymentsUpdated));
    }

    private findItemById(itemId: number): any {
        return find(this.items, (item: any) => item.id === itemId);
    }

    private setEndDateTimeout(): string {
        const { datetime } = this.resource || {};
        let endDateTimeout;

        this.items.forEach(i => {
            if (i.states?.hidden || i.states?.suppressed) {
                return;
            }
            const { end_at: endAt } = i.online || {};

            if (
                endAt &&
                (new Date(endAt) > new Date(endDateTimeout) ||
                    (!endDateTimeout && new Date(endAt) > new Date(datetime)))
            ) {
                endDateTimeout = endAt;
            }
        });

        return endDateTimeout;
    }
}
